import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { TOpenModalPerfilData } from "./SelecaoPerfis";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useQuery } from "react-query";
import axiosClient from "../../../../lib/axios";
import { IDataPerfis } from "../../../../types/api/configuracoes/pesquisa-perfis";
import { IDataLojas } from "../../../../types/api/geral/pesquisar-lojas";
import { TPerfilValue } from "..";
import SelectVendedores from "../../../../components/SelectsSimpleApiQuery/SelectVendedores";
import SelectCondicoesPagamentoCs9 from "../../../../components/SelectsSimpleApiQuery/SelectCondicoesPagamentoCs9";
import {
  formatarInputValorMonetárioSemPrefixo,
  formatarMonetárioParaNúmero,
} from "../../../../helpers/masks";
import { TParamExtras } from "../../../../types/api/configuracoes/detalhar-usuario";
import SelectLoja from "../../../../components/SelectsSimpleApiQuery/SelectLoja";

export default function DialogCrudPerfil({
  open,
  values,
  valueIndex,
  handleClose,
  onSubmit,
}: DialogCrudPerfilProps) {
  const isEdit = useMemo(() => Boolean(values), [values]);

  const [selectPerfil, setSelectPerfil] = useState<number | null>(
    values?.id || null
  );
  const [selectLoja, setSelectLoja] = useState<TSelectState>(
    values?.loja || null
  );
  const [selectVendedor, setSelectVendedor] = useState<TSelectState>(
    values?.vendedor || null
  );
  const [selectFormaPagamento, setSelectFormaPagamento] = useState<
    IPesquisaCondicoesPagamento[] | null
  >(values?.condicoes_pagamento_blocked || null);

  const [selectLimiteAlcada, setSelectLimiteAlcada] = useState<string | null>(
    values?.limite_alcada ? `${values?.limite_alcada}` : null
  );
  const [selectPublico, setSelectPublico] = useState<string[] | null>(values?.tipo_publico || null);

  //queries
  const { data: perfilResult, isLoading: isLoadingPerfil } = useQuery({
    queryKey: ["pesquisar-perfis"],
    queryFn: () =>
      axiosClient.get("/pesquisar-perfis", {
        params: {
          limit: 200,
        },
      }),
  });
  const { dataPerfis }: IPerfis = useMemo(
    () => ({
      dataPerfis: perfilResult?.data.data || [],
    }),
    [perfilResult]
  );

  const { data: lojaResult, isLoading: isLoadingLoja } = useQuery({
    queryKey: ["pesquisar-lojas"],
    queryFn: () =>
      axiosClient.get("/pesquisar-lojas", {
        params: {
          limit: 200,
        },
      }),
  });
  const { dataLojas }: ILojas = useMemo(
    () => ({
      dataLojas: lojaResult?.data.data || [],
    }),
    [lojaResult]
  );
  //

  //valores importantes
  const {
    perfilSelecionado,
    precisaLojaId,
    precisaVendedorId,
    precisaCondicoesPagamentoId,
    precisaLimiteAlcada,
    precisaPublicoPermitido,
  } = useMemo(() => {
    const itemSelecionado = dataPerfis.find((item) => item.id === selectPerfil);

    let valores = {
      perfilSelecionado: itemSelecionado,
      precisaLojaId: false,
      precisaVendedorId: false,
      precisaCondicoesPagamentoId: false,
      precisaLimiteAlcada: false,
      precisaPublicoPermitido: false,
    };

    if (itemSelecionado?.parametros_obrigatorios) {
      valores.precisaLojaId =
        itemSelecionado.parametros_obrigatorios.includes("loja_id");
      valores.precisaVendedorId =
        itemSelecionado.parametros_obrigatorios.includes("vendedor_id");
      valores.precisaCondicoesPagamentoId =
        itemSelecionado.parametros_obrigatorios.includes(
          "condicoes_pagamento_blocked_ids"
        );
      valores.precisaLimiteAlcada =
        itemSelecionado.parametros_obrigatorios.includes("limite_alcada");
      valores.precisaPublicoPermitido =
        itemSelecionado.parametros_obrigatorios.includes("publicos_permitidos");
    }

    return valores;
  }, [selectPerfil, dataPerfis]);

  const handleChangeSelectValue = useCallback(
    (
      value: string,
      setStateFunction: Dispatch<SetStateAction<TSelectState>>
    ) => {
      const regex = /\[(.*?)\](.*)/;
      const matches = regex.exec(value);

      if (matches) {
        const id = matches[1];
        const nome = matches[2];
        setStateFunction({ id: Number(id), nome });
      }
    },
    []
  );

  const getPublicoLabel = (value: 'A' | 'C' | 'F' | 'E' | string): string => {
    const labels = {
      'A': 'Associado',
      'C': 'Cliente', 
      'F': 'Fornecedor',
      'E': 'Empregado'
    } as const;
    
    return labels[value as keyof typeof labels] || value;
  };

  //funcoes manipuladoras
  const handleSubmit = useCallback(() => {
    if (selectPerfil) {
      let params: TPerfilValue = {
        index: valueIndex !== undefined ? valueIndex : null,
        id: selectPerfil,
        nome: perfilSelecionado?.nome || "",
        parametros_obrigatorios: [],
        loja: null,
        vendedor: null,
        condicoes_pagamento_blocked: null,
        limite_alcada: null,
        tipo_publico: null,
      };

      if (precisaLojaId && selectLoja) {
        params.parametros_obrigatorios.push("loja_id");
        params.loja = {
          id: selectLoja.id,
          nome: selectLoja.nome,
        };
      }
      if (precisaVendedorId && selectVendedor) {
        params.parametros_obrigatorios.push("vendedor_id");
        params.vendedor = {
          id: selectVendedor.id,
          nome: selectVendedor.nome,
        };
      }
      if (precisaCondicoesPagamentoId && selectFormaPagamento?.length) {
        params.parametros_obrigatorios.push("condicoes_pagamento_blocked_ids");
        params.condicoes_pagamento_blocked = selectFormaPagamento.map(
          (cond) => ({
            id: Number(cond.id),
            nome: cond.nome,
          })
        );
      }
      if (precisaLimiteAlcada && selectLimiteAlcada) {
        params.parametros_obrigatorios.push("limite_alcada");
        params.limite_alcada = formatarMonetárioParaNúmero(selectLimiteAlcada);
      }
      if (precisaPublicoPermitido && selectPublico) {
        params.parametros_obrigatorios.push("publicos_permitidos");
        params.tipo_publico = selectPublico.map((publico) => publico);
      }
      onSubmit(params);
      handleClose();
    }
  }, [
    handleClose,
    onSubmit,
    precisaLojaId,
    precisaVendedorId,
    precisaCondicoesPagamentoId,
    selectFormaPagamento,
    selectLoja,
    selectPerfil,
    selectVendedor,
    perfilSelecionado,
    valueIndex,
    precisaLimiteAlcada,
    selectLimiteAlcada,
    precisaPublicoPermitido,
    selectPublico,
  ]);
  //

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle id="alert-dialog-title">Vincular perfil</DialogTitle>

      <DialogContent>
        <FormControl fullWidth margin="normal">
          <InputLabel id="perfil_select">
            {isLoadingPerfil ? "Carregando" : "Perfil"}
          </InputLabel>
          <Select
            labelId="perfil_select"
            value={selectPerfil}
            disabled={isLoadingPerfil}
            label="Perfil"
            onChange={(e) => setSelectPerfil(e.target.value as number)}
          >
            {dataPerfis.map((perfil) => (
              <MenuItem key={perfil.id} value={perfil.id}>
                {perfil.nome}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {(precisaLojaId ||
          precisaVendedorId ||
          precisaCondicoesPagamentoId ||
          precisaLimiteAlcada ||
          precisaPublicoPermitido) && (
          <Box mt={2}>
            <Typography variant="h6">Informações extras</Typography>
            <Typography variant="caption">
              Informe abaixo vinculos relativos a esse usuário no perfil
            </Typography>
            <Stack direction="column" gap={1}>
              {precisaLojaId && (
                <SelectLoja
                  value={selectLoja}
                  onChange={setSelectLoja}
                  required
                />
              )}
              {precisaVendedorId && (
                <SelectVendedores
                  value={selectVendedor}
                  onChange={setSelectVendedor}
                  required
                />
              )}
              {precisaCondicoesPagamentoId && (
                <SelectCondicoesPagamentoCs9
                  value={selectFormaPagamento}
                  onChange={setSelectFormaPagamento}
                />
              )}
              {precisaLimiteAlcada && (
                <TextField
                  type="text"
                  label="Limite de Alçada"
                  value={selectLimiteAlcada}
                  onChange={(e) =>
                    setSelectLimiteAlcada(
                      formatarInputValorMonetárioSemPrefixo(e.target.value)
                    )
                  }
                />
              )}
              {precisaPublicoPermitido && (
                <FormControl fullWidth margin="normal">
                  <InputLabel id="publico_select">Público Permitido</InputLabel>
                  <Select
                    labelId="publico_select"
                    label="Público Permitido"
                    multiple
                    value={selectPublico || []}
                    onChange={(e) => setSelectPublico(e.target.value as string[])}
                  >
                    <MenuItem value="A">Associado</MenuItem>
                    <MenuItem value="C">Cliente</MenuItem>
                    <MenuItem value="F">Fornecedor</MenuItem>
                    <MenuItem value="E">Empregado</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Stack>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button
          autoFocus
          variant="contained"
          onClick={handleSubmit}
          disabled={!selectPerfil}
        >
          {isEdit ? "Atualizar" : "Vincular"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface DialogCrudPerfilProps {
  open: boolean;
  values: TOpenModalPerfilData;
  valueIndex: number | undefined;
  handleClose: () => void;
  onSubmit: (valor: TPerfilValue) => void;
}

interface IPerfis {
  dataPerfis: IDataPerfis[];
}

interface ILojas {
  dataLojas: IDataLojas[];
}

type TSelectState = {
  id: number;
  nome: string;
} | null;

export interface IPesquisaCondicoesPagamento {
  id: number;
  nome: string;
}

export const defValueCondicoesPagamentor: IPesquisaCondicoesPagamento = {
  id: 0,
  nome: "",
};